import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select, Textarea } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { callReportUser } from '@/lib/callables';
import { uploadReportEvidence } from '@/lib/uploadEvidence';
import { useAuth } from '@/stores/auth';
import { NATIONAL_EMERGENCY, REPORT_REASONS, type ReportReason } from '@makhe/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultReason?: ReportReason;
  target: {
    targetUserId?: string;
    targetServiceId?: string;
    targetReviewId?: string;
    targetBookingId?: string;
  };
}

export function ReportModal({ open, onClose, defaultReason, target }: Props) {
  const { firebaseUser } = useAuth();
  const [reason, setReason] = useState<ReportReason>(defaultReason ?? 'other');
  const [description, setDescription] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(defaultReason ?? 'other');
      setDescription('');
      setEvidenceFiles([]);
    }
  }, [open, defaultReason]);

  const submit = async () => {
    if (description.trim().length < 10) {
      return toast.error('Tell us at least a sentence.');
    }
    if (!firebaseUser) return toast.error('Sign in to report.');
    setBusy(true);
    try {
      const evidenceURLs =
        evidenceFiles.length > 0
          ? await uploadReportEvidence(evidenceFiles, firebaseUser.uid)
          : undefined;
      await callReportUser({ ...target, reason, description, evidenceURLs });
      toast.success('Reported. Thank you — we will look at it.');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Report a problem">
      <div className="space-y-3">
        <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs">
          <p className="font-medium text-makhe-ink">In immediate danger?</p>
          <p className="mt-1 text-makhe-muted">
            Call SAPS{' '}
            <a href={`tel:${NATIONAL_EMERGENCY.saps}`} className="font-medium text-makhe-primary underline">
              {NATIONAL_EMERGENCY.saps}
            </a>{' '}
            first. Use this form after you are safe.
          </p>
        </div>
        <Select
          label="What happened?"
          value={reason}
          onChange={(e) => setReason(e.target.value as ReportReason)}
          options={REPORT_REASONS.map((r) => ({ value: r, label: r.replace(/_/g, ' ') }))}
        />
        <Textarea
          label="Tell us more"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <label className="block text-sm text-makhe-ink/80">
          Evidence (optional — screenshots, photos)
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={(e) => setEvidenceFiles(Array.from(e.target.files ?? []))}
            className="mt-2 block w-full text-sm text-makhe-ink/70 file:mr-3 file:rounded-full file:border-0 file:bg-makhe-line/50 file:px-4 file:py-2 file:text-makhe-ink"
          />
          {evidenceFiles.length > 0 && (
            <span className="mt-1 block text-xs text-makhe-muted">
              {evidenceFiles.length} file{evidenceFiles.length === 1 ? '' : 's'} selected
            </span>
          )}
        </label>
        <Button onClick={submit} loading={busy} className="w-full">
          Submit report
        </Button>
      </div>
    </Modal>
  );
}
